import { AlertV2, ButtonV2, Dialog, Spinner } from '@cimplico/components';
import { ClockIcon, TagIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useActivateSubscription } from '../../../../queries/billing/use-activate-subscription';
import { SubscriptionV2 } from '../../../../types/firm';
import { formatDate, calcRelativeTime } from '../../../../utilities/formatting';

type TrialAlertProps = {
    subscription: SubscriptionV2;
};

function TrialAlert({ subscription }: TrialAlertProps): JSX.Element | null {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [outcome, setShowOutcome] = useState<'success' | 'error' | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const query = useActivateSubscription();

    function activate(): void {
        setShowConfirmation(false);
        query.mutate(undefined, {
            onSuccess: () => {
                setShowOutcome('success');
            },

            onError: (err) => {
                setShowOutcome('error');
                const msg = err?.response?.data?.errors?.at(0)?.detail ?? 'Unknown error occurred.';
                setErrorMessage(msg);
            },
        });
    }

    if (subscription.status !== 'TRIALING' && !outcome) return null;

    if (outcome === 'success') {
        return <AlertV2 type="success" className="mb-4" title="Your subscription has been activated successfully!"></AlertV2>;
    }
    if (outcome === 'error') {
        return (
            <AlertV2
                type="error"
                className="mb-4"
                title="An error occurred while activating your subscription."
                description={[errorMessage]}
                onClose={(): void => {
                    setShowOutcome(null);
                    setErrorMessage('');
                }}
            ></AlertV2>
        );
    }

    return (
        <>
            {showConfirmation && (
                <Dialog
                    type="info"
                    title="Activate Subscription"
                    description="Activate your subscription now? You'll be charged immediately based on your current subscription configuration."
                    isOpen
                    primaryAction="Activate"
                    secondaryAction="Close"
                    onPrimaryActionClick={activate}
                    onSecondaryActionClick={(): void => setShowConfirmation(false)}
                    onClose={(): void => setShowConfirmation(false)}
                />
            )}
            <div className="rounded-md border border-indigo-400 bg-indigo-50 w-full p-4 mb-4 flex items-center justify-between">
                {query.isLoading ? (
                    <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <p className="text-sm font-bold text-contentPrimary">Activating...</p>
                    </div>
                ) : subscription.trialEnd ? (
                    <p className="text-sm font-bold text-contentPrimary flex items-center gap-1">
                        <ClockIcon className="text-indigo-600 h-5 w-5 stroke-2" />
                        Trial ends: {formatDate(subscription.trialEnd)} ({calcRelativeTime(subscription.trialEnd)})
                    </p>
                ) : (
                    <p className="text-sm font-bold text-contentPrimary flex items-center gap-1">
                        Unable to display trial data - please refresh the page. If the issue persists please contact support.
                    </p>
                )}
                {!query.isLoading && (
                    <ButtonV2 size="sm" label="Subscribe now" leftIcon={<TagIcon />} onClick={(): void => setShowConfirmation(true)} />
                )}
            </div>
        </>
    );
}

export default TrialAlert;
